import React, { Component, Fragment } from 'react';
import { View, ScrollView, RefreshControl } from 'react-native';
import { observer } from 'mobx-react';
import moment from 'moment';

import Modal from '../shared/Modal';
import Text from '../shared/Text';
import IconButton from '../shared/IconButton';

import { IS_WEB } from '../../lib/appConstants';

import {
  TOUCHABLE_MORE,
  TOUCHABLE_4,
  TOUCHABLE_7,
  MARGIN_12,
  MARGIN_8,
  MARGIN_5,
  GOLDEN200,
  pOrT,
  BASE_COLOR,
} from '../styleConstants';

import { getCombats, getCombat } from '../../lib/dbCalls';

import { joinCombat as joinCombatService } from '../../lib/apiCalls';

import { CombatType } from '../../lib/types';

import { action, t } from '../../utils/appUtils';

import WarriorsLine from '../common/WarriorsLine';

import appStore from '../../stores/app';

import warriorInfoModalStore from '../common/WarriorInfoModal/store';

import LogModal from './CombatLogModal/index';

import heroStore from '../../stores/hero';
import {
  validateForCombat,
  validateCombatToJoin,
  getCombatWarrior,
  getOpponentTeam,
} from '../../utils/combatUtils';

import logImport from '../../lib/log';
const log = logImport as (name: string, params?: Record<string, any>) => void;

export async function joinCombat(
  id: string,
  { team, warriorId }: { team?: number; warriorId?: string },
  attack?: boolean,
) {
  const combat = await getCombat(id);
  if (warriorId) {
    const combatWarrior = getCombatWarrior(combat, warriorId);
    team = getOpponentTeam(combatWarrior.team);
  }

  const validate = validateCombatToJoin(combat, t, attack);
  if (validate) return validate;

  joinCombatService(id, team as number);

  return undefined;
}

type Props = {
  onHide: () => void;
};

type State = {
  combats: CombatType[];
  showLogModal: boolean;
  fetching: boolean;
};

@observer
export default class CombatsModal extends Component<Props, State> {
  state: State = {
    combats: [],
    fetching: true,
    showLogModal: false,
  };

  selectedCombat!: CombatType;

  fetch = async () => {
    this.setState({ fetching: true });
    const combats = await getCombats();
    this.setState({
      combats,
      fetching: false,
    });
  };

  onToggleLogModal = (combat: CombatType) => {
    this.selectedCombat = combat;
    this.setState((state) => ({
      showLogModal: !state.showLogModal,
    }));
  };

  onJoin = async (id: string, team: number) => {
    const validate = validateForCombat(heroStore.hero, t);
    if (validate) {
      appStore.showMessage({ type: 'error', text: validate });
      return;
    }

    appStore.toggleLoading(true);
    const result = await joinCombat(id, { team });
    log('joinCombat', {
      warriorLogin: heroStore.hero.login,
      combatId: id,
      team,
    });
    if (typeof result === 'string') {
      appStore.showMessage({ type: 'error', text: result });
      appStore.toggleLoading(false);
      this.fetch();
    }
  };

  render() {
    const { onHide } = this.props;
    const { combats, showLogModal, fetching } = this.state;
    const { coordinateX, coordinateY } = heroStore.hero.location;

    return (
      <Modal
        onHide={onHide}
        fullScreen
        overlay={Boolean(warriorInfoModalStore.warrior) || showLogModal}
        onShow={this.fetch}
        warriorListener
        title={
          <View
            style={{
              width: 200,
              height: 50,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text bold style={{ marginTop: 5, color: GOLDEN200, fontSize: 14 }}>
              {t('combat.combats')}
            </Text>
            <Text
              style={{ color: GOLDEN200, fontSize: 12 }}
            >{`${coordinateX}:${coordinateY}`}</Text>
          </View>
        }
      >
        <View style={{ padding: 30, marginTop: 20, flex: 1 }}>
          <View style={{ flexDirection: 'row' }}>
            {IS_WEB ? (
              <IconButton
                onPress={() => action(this.fetch)}
                style={{ marginTop: -30 }}
                size={14}
                title={t('refresh')}
                source={require('../../assets/images/refresh.png')}
                iconStyle={{ width: 26, height: 26 }}
                name="refresh"
              />
            ) : null}
          </View>
          <ScrollView
            refreshControl={
              <RefreshControl
                tintColor={BASE_COLOR}
                refreshing={fetching}
                onRefresh={() => action(this.fetch)}
              />
            }
            style={{ height: '100%' }}
          >
            {!fetching && !combats.length && <Text bold>{t('combat.noCombats')}</Text>}
            {combats.map((combat, index) => (
              <View key={combat.id} style={{ marginTop: index ? MARGIN_12 : 0 }}>
                <WarriorsLine
                  combat={combat}
                  fetchWarrior
                  beforeRenderer={() => (
                    <Text bold style={{ marginRight: MARGIN_5 }}>
                      {moment(combat.created).format('MMM Do hh:mm')}
                    </Text>
                  )}
                  afterTeamRenderer={(team: number) =>
                    team === 1 ? (
                      <IconButton
                        style={{ marginLeft: MARGIN_8, marginTop: -2 }}
                        name="combatJoin"
                        title={t('combat.join')}
                        size={16}
                        iconStyle={pOrT(
                          {
                            width: 22,
                            height: 22,
                          },
                          {
                            width: 26,
                            height: 26,
                          },
                        )}
                        source={require('../../assets/images/combatJoin.png')}
                        onPress={() => this.onJoin(combat.id, team)}
                        hitSlop={{
                          left: 0,
                          right: TOUCHABLE_MORE,
                          top: TOUCHABLE_4,
                          bottom: TOUCHABLE_7,
                        }}
                      />
                    ) : (
                      <Fragment>
                        <IconButton
                          style={{ marginLeft: 8, marginTop: -2 }}
                          name="combatJoin"
                          size={16}
                          title={t('combat.join')}
                          iconStyle={pOrT(
                            {
                              width: 22,
                              height: 22,
                            },
                            {
                              width: 26,
                              height: 26,
                            },
                          )}
                          source={require('../../assets/images/combatJoin.png')}
                          onPress={() => this.onJoin(combat.id, team)}
                          hitSlop={{
                            right: TOUCHABLE_4,
                            left: 0,
                            top: TOUCHABLE_4,
                            bottom: TOUCHABLE_7,
                          }}
                        />
                        <IconButton
                          style={{ marginLeft: 8, marginTop: -2 }}
                          name="combatLog"
                          title={t('combat.combatLog')}
                          iconStyle={pOrT(
                            {
                              width: 22,
                              height: 22,
                            },
                            {
                              width: 26,
                              height: 26,
                            },
                          )}
                          source={require('../../assets/images/log.png')}
                          size={16}
                          onPress={() => this.onToggleLogModal(combat)}
                          hitSlop={{
                            left: 0,
                            right: TOUCHABLE_MORE,
                            top: TOUCHABLE_4,
                            bottom: TOUCHABLE_7,
                          }}
                        />
                      </Fragment>
                    )
                  }
                />
              </View>
            ))}
          </ScrollView>
        </View>
        {showLogModal ? (
          <LogModal combat={this.selectedCombat} onHide={this.onToggleLogModal as any} />
        ) : null}
      </Modal>
    );
  }
}
